import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, AlertTriangle } from 'lucide-react';
import { COMPANY, PRODUCT } from '@/lib/company';

const LAST_UPDATED = '2026-06-20';
const CONTACT_EMAIL = PRODUCT.support_email;
const APP = PRODUCT.name;
const COMPANY_NAME = COMPANY.legal_name;
const COMPANY_WEBSITE = COMPANY.website;

const PRIVACY = (
  <article className="space-y-5 text-sm leading-relaxed text-[#4A5A50]">
    <p>
      {APP} is an AI-powered travel companion that surfaces real-time travel utility
      information — weather, air quality, transit, currency, nearby essentials,
      translation and an AI concierge — so you can plan less and experience more.
      We are committed to keeping the experience private and lightweight: minimal
      data, no advertising profile, no persistent location history.
    </p>
    <p>
      {APP} is operated by <b>{COMPANY_NAME}</b>, a company incorporated in India.
      Parent website: <a className="font-bold text-[#15291C] underline" href={COMPANY_WEBSITE} target="_blank" rel="noreferrer">www.nevika.co</a>.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">1. What we collect</h2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>
        <b>Approximate location</b> (latitude / longitude) only when you tap
        &quot;Use my location&quot; or use a feature that needs it (Map, Transit,
        Essentials). Coordinates are processed in memory to fetch nearby data
        and are <em>not written to any server-side database</em>.
      </li>
      <li>
        <b>Selected city</b> (name, country, coordinates) is saved locally in
        your browser so {APP} remembers your context between visits. You can
        clear it anytime from your browser/app settings.
      </li>
      <li>
        <b>AI chat history</b> per anonymous session (kept up to 30 days in our
        database for context continuity, then auto-deleted via a MongoDB TTL).
      </li>
      <li>
        <b>Account profile</b> (name, email, avatar) <em>only</em> if you choose
        to sign in with Google via Emergent-managed OAuth. We never see your
        Google password.
      </li>
      <li>
        <b>Aggregated, non-identifying analytics</b> (page views, error rates)
        with session recording disabled on sensitive screens.
      </li>
    </ul>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">2. What we do NOT collect</h2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>No advertising IDs, no behavioural ad tracking.</li>
      <li>No persistent storage of your location history on our servers.</li>
      <li>No reading or sharing of your contact list, photos, microphone or files
        beyond the single utterance you actively speak into the voice composer.</li>
      <li>Voice input is processed by the browser&apos;s on-device Web Speech API and
        is not transmitted anywhere by {APP}.</li>
    </ul>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">3. Third-party data providers</h2>
    <p>
      To render the live information you see, {APP} queries licensed third-party
      providers on your behalf. For each request we send only the coordinates
      required to answer the question (e.g. &quot;weather near 48.85, 2.35&quot;) — we
      never share your name, email or device identity with any provider. A
      current list of our sub-processors is available on request at the address
      below. Current providers include: OpenWeatherMap, Open-Meteo, met.no,
      Frankfurter (FX), Foursquare, Google Places, HERE, OpenStreetMap /
      Overpass, Calendarific, TransitApp, NewsAPI, GNews, NewsData, OpenRouter
      and Emergent (LLM inference).
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">4. Cookies &amp; storage</h2>
    <p>
      We use browser <code>localStorage</code> only for: your saved city,
      favourites, visited countries, AI thread metadata, trip diary entries and
      UI preferences. We do not set tracking cookies. Our analytics provider
      may set first-party cookies for anonymised session continuity. When
      signed in, a single httpOnly session cookie maintains your login.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">5. AI &amp; content</h2>
    <p>
      Trivly uses large language models (OpenRouter / Emergent) to generate
      itineraries, alerts, safety overlays and chat replies. AI responses are
      best-effort suggestions and may be incorrect. Always verify visas,
      prices, medical advice and safety information with official sources.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">6. Children</h2>
    <p>
      {APP} is intended for general audiences and does not knowingly collect
      data from children under 13. If you believe a minor has provided personal
      information, contact us and we will remove it.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">7. Your choices</h2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Decline location at the prompt — {APP} falls back to a default city.</li>
      <li>Use the in-app &quot;Clear history&quot; to wipe your AI chat session.</li>
      <li>Sign out from Profile to delete your active session cookie.</li>
      <li>Clear your browser/app storage to delete everything stored locally.</li>
      <li>Email us to request deletion of your cloud-stored profile + history.</li>
    </ul>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">8. Data retention</h2>
    <p>
      Locally-stored data persists until you clear it. Cloud data: AI chat
      messages auto-expire after 30 days. Account profiles persist until you
      request deletion at the email below.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">9. Contact</h2>
    <p>
      Questions, deletion requests, or to report abuse:{' '}
      <a className="font-bold text-[#15291C] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
    </p>

    <div className="mt-4 rounded-2xl border border-[#0F1A13]/10 bg-white/60 p-4" data-testid="corporate-credentials">
      <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15291C]">Corporate credentials</p>
      <p className="mt-2 font-display text-base font-bold text-[#0F1A13]">{COMPANY.legal_name}</p>
      <p className="text-[12px] text-[#4A5A50] italic mt-0.5">{COMPANY.tagline}</p>

      <dl className="mt-3 text-[12.5px] grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
        <dt className="font-semibold text-[#0F1A13]">CIN</dt>
        <dd className="text-[#4A5A50]">{COMPANY.cin}</dd>
        <dt className="font-semibold text-[#0F1A13]">GSTIN</dt>
        <dd className="text-[#4A5A50]">{COMPANY.gstin}</dd>
        <dt className="font-semibold text-[#0F1A13]">Website</dt>
        <dd>
          <a className="text-[#15291C] underline break-all" href={COMPANY.website} target="_blank" rel="noreferrer">
            {COMPANY.website.replace(/^https?:\/\//, '')}
          </a>
        </dd>
        <dt className="font-semibold text-[#0F1A13]">Email</dt>
        <dd>
          <a className="text-[#15291C] underline break-all" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
        </dd>
        <dt className="font-semibold text-[#0F1A13]">Mobile</dt>
        <dd>
          <a className="text-[#15291C] underline" href={`tel:${COMPANY.phone.replace(/\s+/g,'')}`}>{COMPANY.phone}</a>
          <span className="text-[#7A8A80]"> · </span>
          <a className="text-[#15291C] underline" href={`tel:${COMPANY.phone_alt.replace(/\s+/g,'')}`}>{COMPANY.phone_alt}</a>
        </dd>
      </dl>

      <p className="mt-3 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15291C]">Registered office</p>
      <address className="not-italic mt-1 text-[12.5px] text-[#4A5A50] leading-relaxed">
        {COMPANY.registered_office.line1},<br />
        {COMPANY.registered_office.line2},<br />
        {COMPANY.registered_office.area},<br />
        {COMPANY.registered_office.city},<br />
        Mumbai – {COMPANY.registered_office.postal_code},<br />
        {COMPANY.registered_office.state}, {COMPANY.registered_office.country}
      </address>
    </div>
  </article>
);

const TERMS = (
  <article className="space-y-5 text-sm leading-relaxed text-[#4A5A50]">
    <p>
      By using {APP} you agree to these terms. The service is provided &quot;as is&quot;,
      with no warranty of accuracy. Always verify critical decisions (medical,
      emergency, transit, visa, financial) with the relevant authority or
      service provider directly.
    </p>
    <p>
      {APP} is offered by <b>{COMPANY_NAME}</b> (parent website:{' '}
      <a className="underline" href={COMPANY_WEBSITE} target="_blank" rel="noreferrer">www.nevika.co</a>).
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">1. Acceptable use</h2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Personal and non-commercial travel-utility use.</li>
      <li>Don&apos;t scrape, bulk-export, or rebrand our data feeds, APIs or AI outputs.</li>
      <li>Don&apos;t attempt to overload, breach or reverse-engineer the service.</li>
      <li>Don&apos;t use {APP} for illegal activities, harassment or to generate
        deceptive content about real places, people or businesses.</li>
    </ul>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">2. Third-party data</h2>
    <p>
      Real-time information is supplied by external providers. Each remains
      owned by its provider and may be subject to its own terms. {APP} is not
      liable for inaccuracies, gaps in coverage or service interruptions.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">3. AI disclaimer</h2>
    <p>
      {APP} uses AI models to generate itineraries, summaries and replies.
      Outputs are best-effort and may contain errors, biases or hallucinations.
      Do not rely on AI output for medical, legal, financial or safety-critical
      decisions without verifying with an authoritative source.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">4. Emergency disclaimer</h2>
    <p className="rounded-2xl bg-amber-400/15 border border-[#F6C84F] text-[#0F1A13] p-3">
      <AlertTriangle className="w-4 h-4 inline mr-1 text-[#B85F31]" />
      In a real emergency, dial your local emergency number. {APP}&apos;s nearby
      hospitals / police listings are a convenience, not a substitute for
      calling official emergency services.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">5. Booking</h2>
    <p>
      {APP} does not currently sell flights, hotels, eSIMs or cabs directly.
      Booking links may hand off to third-party sites (e.g. gotravlr.com); any
      transaction is governed by that site&apos;s own terms. {APP} earns no
      commission unless explicitly disclosed.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">6. Liability</h2>
    <p>
      To the maximum extent permitted by law, {APP}, {COMPANY_NAME} and its
      operators shall not be liable for any indirect, incidental or
      consequential damages arising from your use of the service.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">7. Governing law</h2>
    <p>
      These terms are governed by the laws of India. Any dispute will be
      subject to the exclusive jurisdiction of the competent courts at the
      registered office of {COMPANY_NAME}.
    </p>

    <h2 className="text-base font-extrabold text-[#0F1A13] mt-6">8. Changes</h2>
    <p>
      We may update these terms. Material changes will be posted here with a
      new &quot;Last updated&quot; date. Continued use constitutes acceptance.
    </p>
  </article>
);

export default function Legal() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const isPrivacy = kind === 'privacy';
  const title = isPrivacy ? 'Privacy Policy' : 'Terms of Service';
  const Icon = isPrivacy ? ShieldCheck : FileText;

  return (
    <div className="min-h-dvh bg-[#F2F0E4]" data-testid={`legal-${isPrivacy ? 'privacy' : 'terms'}`}>
      <div className="px-5 pt-5 pb-3 flex items-center gap-3 border-b border-[#0F1A13]/10 bg-white sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[#0F1A13]/5 flex items-center justify-center hover:bg-[#0F1A13]/10 transition-colors gt-focus"
          aria-label="Back"
          data-testid="legal-back"
        >
          <ArrowLeft className="w-4 h-4 text-[#0F1A13]" />
        </button>
        <span className="flex items-center gap-2">
          <img src="/brand/trivly-app-icon.png" alt="" className="w-6 h-6 rounded-md" />
          <span className="font-display text-base font-bold text-[#0F1A13] tracking-tight">Trivly</span>
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0F1A13]/5 border border-[#0F1A13]/10">
          <Icon className="w-3.5 h-3.5 text-[#15291C]" />
          <span className="font-bold text-[12px] text-[#0F1A13]">{title}</span>
        </span>
      </div>

      <div className="px-5 pt-6 pb-16 max-w-2xl mx-auto">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0F1A13]">{title}</h1>
        <p className="text-xs text-[#7A8A80] mt-1 font-medium">Last updated: {LAST_UPDATED}</p>

        <div className="mt-6">
          {isPrivacy ? PRIVACY : TERMS}
        </div>
      </div>
    </div>
  );
}
